/* ================================================================
   rq-no-autoscroll.js

   Stops the product page from scrolling itself down to the variant
   picker and then back up to the top on load.

   WHY IT HAPPENS (three links in one chain)

   1. sections/main-product.liquid runs a block at DOMContentLoaded
      that force-selects the BLACK / XL variant whenever the URL has
      no ?variant=. It does that by setting radio.checked and firing a
      synthetic change event. No user touched anything.

   2. That change reaches Dawn's assets/product-info.js, which after
      swapping the section HTML does:

          .then(() => { document.querySelector('#'+targetId)?.focus() })

      Focusing an off-screen form control makes the browser scroll it
      into view. targetId is the XL pill, so the page jumps DOWN to
      the variant picker. Stock Dawn, and it has no preventScroll.

   3. The same swap calls media-gallery.js setActiveMedia(), which had
      already queued a window.setTimeout containing:

          if (activeMediaRect.top > -0.5) return;
          window.scrollTo({ top: rect.top + window.scrollY, behavior: 'smooth' })

      The focus in step 2 is a microtask and runs FIRST, so by the
      time this timer fires the featured image is far above the
      viewport, the early return is skipped, and it smooth-scrolls
      back UP. Hence down, then up.

   THE FIX
   Nothing should scroll the page before the visitor has touched it.
   So for the first few seconds after load, and only until the first
   real gesture, scrollTo / scrollBy / scrollIntoView become no-ops
   and focus() is forced to preventScroll. Focus still moves, so
   keyboard and screen-reader behaviour is unchanged; only the
   scrolling side effect is dropped. The originals are put back the
   moment the visitor touches the page, so every deliberate scroll
   after that works normally.

   This runs WITHOUT defer, from the <head>, so it is installed before
   product-info.js and media-gallery.js (both modules, therefore
   deferred) ever execute.

   NOTE: this treats the symptom. The root cause is the force-select
   block in sections/main-product.liquid. If the BLACK / XL default is
   dropped, or set through a ?variant= link instead of a synthetic
   change event, this file can be removed.
   ================================================================ */
(function () {
  'use strict';

  if (window.__rqNoAutoScroll) return;
  window.__rqNoAutoScroll = true;

  /* Generous enough to cover a slow variant fetch on mobile data. */
  var WINDOW_MS = 3500;

  var start = Date.now();
  var armed = true;

  var origScrollTo = window.scrollTo;
  var origScrollBy = window.scrollBy;
  var origSIV = Element.prototype.scrollIntoView;
  var origFocus = HTMLElement.prototype.focus;

  function suppressing() {
    return armed && (Date.now() - start) < WINDOW_MS;
  }

  window.scrollTo = function () {
    if (suppressing()) return;
    return origScrollTo.apply(window, arguments);
  };

  window.scrollBy = function () {
    if (suppressing()) return;
    return origScrollBy.apply(window, arguments);
  };

  Element.prototype.scrollIntoView = function () {
    if (suppressing()) return;
    return origSIV.apply(this, arguments);
  };

  HTMLElement.prototype.focus = function () {
    if (suppressing()) {
      /* Keep the focus, drop the scroll. */
      try { return origFocus.call(this, { preventScroll: true }); }
      catch (e) { return origFocus.call(this); }
    }
    return origFocus.apply(this, arguments);
  };

  function restore() {
    if (!armed) return;
    armed = false;
    window.scrollTo = origScrollTo;
    window.scrollBy = origScrollBy;
    Element.prototype.scrollIntoView = origSIV;
    HTMLElement.prototype.focus = origFocus;
  }

  /* The first real gesture ends the window immediately. Capture phase
     so we win the race against the handler that acts on the tap. */
  var events = ['pointerdown', 'touchstart', 'mousedown', 'keydown', 'wheel'];
  for (var i = 0; i < events.length; i++) {
    window.addEventListener(events[i], restore, { once: true, passive: true, capture: true });
  }

  setTimeout(restore, WINDOW_MS);
})();
